import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Rocket, 
  Map, 
  Upload, 
  Trophy, 
  Users, 
  ExternalLink,
  Loader2,
  FileDown
} from "lucide-react";

interface GettingStartedStepProps {
  onComplete: () => void;
  isLoading?: boolean;
}

export const GettingStartedStep = ({ onComplete, isLoading }: GettingStartedStepProps) => {
  const platformFeatures = [
    {
      icon: <Map className="w-5 h-5" />,
      title: "Explore Trails",
      description: "Discover thousands of off-road trails on our interactive map with difficulty ratings and conditions.",
    },
    {
      icon: <Upload className="w-5 h-5" />,
      title: "Log Your Runs",
      description: "Upload GPX tracks from your adventures. We'll analyze your route and match it to known trails.",
    },
    {
      icon: <Trophy className="w-5 h-5" />,
      title: "Earn Badges",
      description: "Complete trails, hit milestones, and earn badges as you explore more terrain.",
    },
    {
      icon: <Users className="w-5 h-5" />,
      title: "Join the Community",
      description: "Connect with fellow off-roaders, follow their adventures, and join group runs.",
    },
  ];

  return (
    <Card>
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Rocket className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="text-2xl">You're All Set!</CardTitle>
        <CardDescription>
          Here's a quick overview of what you can do on Airing Down.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Platform overview */}
        <div className="grid gap-4">
          {platformFeatures.map((feature, index) => (
            <div key={index} className="flex gap-4 items-start">
              <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                {feature.icon}
              </div>
              <div>
                <h3 className="font-medium">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* GPX explainer */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FileDown className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium mb-1">New to GPX Tracks?</h3>
              <p className="text-sm text-muted-foreground mb-3">
                GPX files record your location as you drive, creating a detailed log of your route. 
                Most off-road apps can export GPX files after your adventure.
              </p>
              <Link 
                to="/help/gpx-tracks"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                Learn how to record GPX tracks
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Beta acknowledgment */}
        <p className="text-xs text-muted-foreground text-center">
          Airing Down is in <strong>Beta</strong>. By continuing, you acknowledge our{' '}
          <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link> and{' '}
          <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
        </p>

        <Button
          onClick={onComplete}
          disabled={isLoading}
          className="w-full"
          size="lg"
          variant="hero"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Getting Ready...
            </>
          ) : (
            <>
              <Rocket className="w-4 h-4 mr-2" />
              Start Exploring
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
